import Accordion from '@precis-digital/kurama/Accordion';
import AttachmentTile from '@precis-digital/kurama/AttachmentTile';
import Grid from '@precis-digital/kurama/Grid';
import ChevronDownIcon from '@precis-digital/kurama/icons/ChevronDownIcon';
import DatatableIcon from '@precis-digital/kurama/icons/DatatableIcon';
import ImageIcon from '@precis-digital/kurama/icons/ImageIcon';
import NoDatatableIcon from '@precis-digital/kurama/icons/NoDatatableIcon';
import Typography from '@precis-digital/kurama/Typography';
import { styled } from '@precis-digital/kurama/utils';
import { useCallback, useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { type JustSentImagePreview } from 'shared/copilot/hooks/useCopilotAgentConversation';
import { revokePreviewUrl } from 'shared/copilot/hooks/useCopilotImageAttachments';
import { type AttachedMessageImage } from 'shared/copilot/utils/imageAttachment';
import { useTranslation } from 'shared/translations';
import { CopilotImageLightbox } from './CopilotImageLightbox';

/** Lets the layout test assert the CSV and image tiles share one row. */
export const USER_MESSAGE_ATTACHMENT_ROW_TEST_ID = 'copilot-user-message-attachment-row';
/** Only one CSV tile can ever be attached, so it needs no per-file suffix. */
export const USER_MESSAGE_CSV_TILE_TEST_ID = 'copilot-user-message-csv-tile';
/** One per image, keyed by its gcsUri — lets tests target/hover a specific tile without relying on its tooltip text. */
export const userMessageImageTileTestId = (gcsUri: string): string => `copilot-user-message-image-tile-${gcsUri}`;

/** Returns the same map when that URI was never stored, so React can skip the render. */
const withoutRefreshedUrl = (urls: Record<string, string>, gcsUri: string): Record<string, string> => {
  if (urls[gcsUri] == null) return urls;
  return Object.fromEntries(Object.entries(urls).filter(([uri]) => uri !== gcsUri));
};

/**
 * The batch URL to show instead of a stored refresh, once the batch has
 * signed something newer than both the refresh and the URL that refresh
 * replaced. Equal URLs are the cache write from `refreshPreviewUrl` itself.
 */
const supersedingBatchUrl = (gcsUri: string, refreshedUrlByGcsUri: Readonly<Record<string, string>>, previewUrlByGcsUri: Readonly<Record<string, string>>, batchUrlWhenRefreshedByGcsUri: ReadonlyMap<string, string | null>): string | null => {
  const refreshedUrl = refreshedUrlByGcsUri[gcsUri];
  const batchUrl = previewUrlByGcsUri[gcsUri];
  if (refreshedUrl == null || batchUrl == null || !batchUrlWhenRefreshedByGcsUri.has(gcsUri)) return null;
  const batchAtRefresh = batchUrlWhenRefreshedByGcsUri.get(gcsUri);
  if (batchUrl === refreshedUrl || batchUrl === batchAtRefresh) return null;
  return batchUrl;
};
const StyledAttachmentRow = styled(Grid)(({
  theme
}) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1)
}));

/** The gap from the message text above down to the tile row or the accordion. */
const StyledAttachmentsContainer = styled('div')(({
  theme
}) => ({
  marginTop: theme.spacing(1)
}));

/**
 * Kurama's Accordion is `width: 100%` by default — it must not stretch across
 * the message bubble. MUI's own AccordionSummary also carries a few defaults
 * this compact inline trigger doesn't want:
 * - `minHeight: 48` on the summary row (64 once expanded, though Kurama
 *   already zeroes that half) — reset to `auto` so the row is only ever as
 *   tall as its own text/icon, one line, not an explicit fixed height.
 * - the summary's text span has `flexGrow: 1`, which — since the accordion's
 *   own fit-content width is really the *widest sibling*, i.e. the tile row
 *   below once it holds more than one tile — stretches the text to that
 *   width and shoves the chevron out to align with the tile row's far edge
 *   instead of sitting right next to the label.
 * - `AccordionDetails` keeps MUI's default 12px left/right/bottom padding
 *   even after Kurama's own top-padding override, so the tile row sits
 *   indented compared to the flush-left summary text above it.
 * - the summary root is a `ButtonBase`, which defaults to
 *   `justifyContent: 'center'` — never reset by MUI's own AccordionSummary
 *   styles. Since the accordion's real (fit-content) width comes from the
 *   tile row once it's wider than the label, that default centers the
 *   label+chevron group inside the wider box instead of pinning it to the
 *   left edge the tile row and the message text above both share.
 * - Kurama's spacing scale starts at 4px, so `theme.spacing(0)` is 4px. The
 *   chevron margin and details padding use a literal `0` to actually flush them.
 */
const StyledAttachmentsAccordion = styled(Accordion)({
  width: 'fit-content',
  '.MuiAccordionSummary-root': {
    minHeight: 'auto',
    justifyContent: 'flex-start'
  },
  '.MuiAccordionSummary-content': {
    flexGrow: 0
  },
  '.MuiAccordionSummary-expandIconWrapper': {
    marginLeft: 0
  },
  '.MuiAccordionDetails-root': {
    padding: 0
  }
});

/**
 * `ChevronDownIcon`'s own svg is a fixed 24x24 and doesn't accept width/height
 * props, so it's scaled down here to `body3`'s 20px line-height instead — the
 * row's height comes from the label's own line box, one line of text, and the
 * icon must never be the taller of the two.
 */
const StyledExpandIcon = styled('span')(({
  theme
}) => ({
  display: 'flex',
  width: theme.spacing(4),
  height: theme.spacing(4),
  '& svg': {
    width: '100%',
    height: '100%'
  }
}));
export interface CopilotUserMessageAttachmentsProps {
  csvFilename: string | null;
  /** Raw `gs://` URI behind `csvFilename` — looked up live against `csvFilenameByGcsUri`
   * to tell a deleted file apart from one whose listing just hasn't loaded yet. */
  csvGcsUri: string | null;
  /** `gcsUri → filename` for the user's currently-uploaded CSV files (same index the
   * composer upserts into on upload, so a just-sent file is already present here). */
  csvFilenameByGcsUri: Readonly<Record<string, string>>;
  /** True while the CSV index is still loading — never mark a file "unavailable" from this alone. */
  isCsvFilenameIndexLoading: boolean;
  /** True if the CSV listing request itself failed — a reachability problem, not proof of deletion. */
  isCsvFilenameIndexError: boolean;
  images: AttachedMessageImage[];
  previewUrlByGcsUri: Readonly<Record<string, string>>;
  /** Real filenames, signed alongside the preview URLs. */
  filenameByGcsUri: Readonly<Record<string, string>>;
  /** True while `previewUrlByGcsUri` is still being signed for this thread. */
  arePreviewUrlsLoading: boolean;
  /**
   * True once signing has exhausted its retries and produced nothing — a
   * failure to reach Flow, never a deleted file (Flow still returns 200 and
   * simply omits a URI it won't sign). Must not be folded into "unavailable":
   * that would turn a transient sign failure into a false "this file was
   * deleted".
   */
  arePreviewUrlsError: boolean;
  /**
   * URIs the batch could not sign and that have no earlier URL to keep
   * showing. Same tile as `arePreviewUrlsError` — a retry, never "deleted" —
   * but scoped to these URIs so a sibling that did sign stays on screen.
   */
  failedPreviewGcsUris?: readonly string[];
  refreshPreviewUrl: (gcsUri: string) => Promise<string | null>;
  /**
   * Signs a URI the batch never held data for, without touching the shared
   * batch cache — see the sign-error retry handler in this component for why
   * `refreshPreviewUrl` is the wrong function for that case. Returns the
   * filename alongside the URL: `filenameByGcsUri` is read off the batch's
   * own (still-erroring) data, which never has this URI's name to give.
   */
  signPreviewUrlWithoutCaching: (gcsUri: string) => Promise<{
    url: string;
    filename?: string;
  } | null>;
  /**
   * Blob preview + real filename for an image this message just sent, keyed
   * by gcsUri — an instant stand-in for the not-yet-signed URL, seeded once
   * on this component's first render and superseded automatically once
   * `previewUrlByGcsUri` actually has this URI (see the cleanup effect
   * below). Empty for a hydrated/older message, which never had one to
   * begin with — the batch's normal loading → success/unavailable path
   * covers that case exactly as it always has.
   */
  justSentImagePreviewByGcsUri?: Readonly<Record<string, JustSentImagePreview>>;
}

/**
 * The attachment tiles above a sent user message: the CSV tile (if any) and
 * every attached image, all in one row that wraps.
 *
 * They share a row — rather than the CSV tile getting one of its own — so a
 * message bubble (which sizes to `fit-content`) widens to fit them side by
 * side instead of stacking two square tiles on top of each other when there
 * happen to be exactly two attachments and little text.
 *
 * Signed image URLs expire after an hour, so a tab left open long enough will
 * hit a dead URL. That failure re-signs the one URI. The file is gone only
 * when that sign comes back empty — Flow omits a URI it will not sign. A
 * successful refresh expires too, and the batch signs again once it is stale
 * (30 minutes), so a stored refresh must not hide a newer batch URL or turn
 * the next image error into "deleted".
 *
 * The initial batch sign request is a different failure from that: it can
 * fail outright (network, Flow down) with no URL for *any* image in the
 * thread. That renders as a retryable error tile, never "deleted" — Flow
 * still answers 200 and simply omits a URI it won't sign, so "deleted" is
 * reserved for the case the request actually succeeded without this URI.
 * Retrying that per-tile (`handleSignErrorRetry`) is scoped to this one URI
 * on both outcomes: success shows the picture without asserting anything
 * about the other tiles in the batch, and failure leaves the tile exactly
 * as retryable as before — a manual retry can never turn a sibling tile
 * into "deleted".
 *
 * The CSV tile has a much simpler unavailable path than an image's: there is
 * no signed-URL lifecycle to retry, just a live lookup of its `gcsUri` against
 * `csvFilenameByGcsUri` (the same "currently uploaded files" index the
 * composer's own upload already upserts into, so a just-sent file is present
 * immediately, with no optimistic-preview dance needed). An absent entry only
 * means "deleted" once that index has actually settled — `isCsvFilenameIndexLoading`
 * and `isCsvFilenameIndexError` gate it exactly like the image side's own
 * `arePreviewUrlsLoading`/`arePreviewUrlsError`, for the same reason: a listing
 * that hasn't loaded yet, or failed to load at all, must never be read as proof
 * the file is gone.
 *
 * Clicking an image thumbnail opens it full screen. The lightbox lives here
 * rather than at the chat root because only one is ever open and it belongs
 * to the message whose picture it shows. The CSV tile never activates — a CSV
 * has no picture to open full screen.
 *
 * A message the composer just sent skips all of that waiting: its images
 * arrive with `justSentImagePreviewByGcsUri` already carrying a local blob
 * preview and the real filename (the composer had both the instant the
 * upload succeeded), seeded once into local state and used ahead of the
 * batch's own (not-yet-signed) data until the batch catches up.
 */
export const CopilotUserMessageAttachments = ({
  csvFilename,
  csvGcsUri,
  csvFilenameByGcsUri,
  isCsvFilenameIndexLoading,
  isCsvFilenameIndexError,
  images,
  previewUrlByGcsUri,
  filenameByGcsUri,
  arePreviewUrlsLoading,
  arePreviewUrlsError,
  failedPreviewGcsUris = [],
  refreshPreviewUrl,
  signPreviewUrlWithoutCaching,
  justSentImagePreviewByGcsUri = {}
}: CopilotUserMessageAttachmentsProps): ReactElement | null => {
  const {
    t
  } = useTranslation('common', {
    keyPrefix: 'copilot.composer.imageAttachment'
  });
  const {
    t: tCsv
  } = useTranslation('common', {
    keyPrefix: 'copilot.composer.csvAttachment'
  });
  const {
    t: tViewer
  } = useTranslation('common', {
    keyPrefix: 'copilot.imageViewer'
  });
  const {
    t: tAttachments
  } = useTranslation('common', {
    keyPrefix: 'copilot.userMessageAttachments'
  });
  const [refreshedUrlByGcsUri, setRefreshedUrlByGcsUri] = useState<Record<string, string>>({});
  // Seeded once from this message's own first render — a later message's
  // just-sent images (which would grow the prop this came from) must never
  // retroactively appear here.
  const [optimisticPreviewByGcsUri, setOptimisticPreviewByGcsUri] = useState<Readonly<Record<string, JustSentImagePreview>>>(justSentImagePreviewByGcsUri);
  /**
   * Filenames from a sign-error retry (`handleSignErrorRetry`), which never
   * reaches the shared batch cache — so it has to carry its own name
   * alongside the URL rather than leaving `resolveFilename` to find it in
   * `filenameByGcsUri`, which the still-erroring batch never populated for
   * this URI.
   */
  const [refreshedFilenameByGcsUri, setRefreshedFilenameByGcsUri] = useState<Record<string, string>>({});
  const [unavailableGcsUris, setUnavailableGcsUris] = useState<string[]>([]);
  /**
   * The thumbnail and the lightbox render the same (possibly expired) `src`
   * for an opened image, so an expired URL fires `onError` from both at
   * once. Without this, both events would race their own independent
   * `refreshPreviewUrl` call for the same `gcsUri` — two real network
   * requests with no shared outcome — and whichever settled last would win,
   * regardless of what the other one found. Tracked as a ref, not state:
   * it is bookkeeping for `handlePreviewError` itself, not something a
   * render should ever depend on.
   */
  const inFlightRefreshByGcsUri = useRef<Map<string, Promise<string | null>>>(new Map());
  /**
   * Batch URL at the moment each refresh was stored. A later batch value is
   * a new sign (the 30-minute refetch); the same value is `refreshPreviewUrl`
   * writing its result back into the shared cache.
   */
  const batchUrlWhenRefreshedByGcsUri = useRef<Map<string, string | null>>(new Map());
  const [openedGcsUri, setOpenedGcsUri] = useState<string | null>(null);

  // Drops and revokes an optimistic preview the instant the batch's own
  // signed URL for that gcsUri arrives — from then on `resolveUrl` reads the
  // authoritative one anyway, so keeping the blob alive any longer would
  // only leak it.
  //
  // Deliberately the *only* place this component ever revokes one of these
  // blobs — there is no unmount cleanup here. This component does not own
  // the blob end-to-end (the composer created it, and it is also kept in
  // the conversation-wide `justSentImagePreviewByGcsUri` this was seeded
  // from), so an unmount is not a reliable "safe to destroy" signal for it:
  // React StrictMode deliberately mounts, unmounts and remounts a fresh
  // component synthetically, and a plain `useEffect` cleanup fires during
  // that fake unmount too — revoking a blob the very next render still
  // needs, which broke the image outright. Supersession by the real signed
  // URL is the only signal that a blob is provably never needed again, and
  // that is content-driven, not lifecycle-driven, so it survives
  // StrictMode's replay unscathed. A conversation this user actually
  // abandons before signing ever completes is cleaned up instead by
  // `useCopilotAgentConversation`'s own thread-switch and unmount handling,
  // which owns these blobs for the entire session rather than per render.
  useEffect(() => {
    const supersededGcsUris = Object.keys(optimisticPreviewByGcsUri).filter(gcsUri => previewUrlByGcsUri[gcsUri] != null);
    if (supersededGcsUris.length === 0) return;
    setOptimisticPreviewByGcsUri(existing => {
      supersededGcsUris.forEach(gcsUri_0 => {
        revokePreviewUrl(existing[gcsUri_0]?.previewUrl ?? null);
      });
      return Object.fromEntries(Object.entries(existing).filter(([gcsUri_1]) => !supersededGcsUris.includes(gcsUri_1)));
    });
  }, [previewUrlByGcsUri, optimisticPreviewByGcsUri]);

  // A stored refresh is only a stand-in until the batch signs again. Drop it
  // once that newer URL arrives so a later expiry of the batch URL can sign
  // once more — leaving the refresh in place made the next error look like
  // the file had been deleted.
  useEffect(() => {
    const supersededGcsUris_0 = Object.keys(refreshedUrlByGcsUri).filter(gcsUri_2 => supersedingBatchUrl(gcsUri_2, refreshedUrlByGcsUri, previewUrlByGcsUri, batchUrlWhenRefreshedByGcsUri.current) != null);
    if (supersededGcsUris_0.length === 0) return;
    supersededGcsUris_0.forEach(gcsUri_3 => {
      batchUrlWhenRefreshedByGcsUri.current.delete(gcsUri_3);
    });
    setRefreshedUrlByGcsUri(existing_0 => {
      const next = Object.fromEntries(Object.entries(existing_0).filter(([gcsUri_4]) => !supersededGcsUris_0.includes(gcsUri_4)));
      return Object.keys(next).length === Object.keys(existing_0).length ? existing_0 : next;
    });
  }, [previewUrlByGcsUri, refreshedUrlByGcsUri]);
  const handlePreviewError = useCallback((gcsUri_5: string) => {
    if (unavailableGcsUris.includes(gcsUri_5)) return;
    const newerBatchUrl = supersedingBatchUrl(gcsUri_5, refreshedUrlByGcsUri, previewUrlByGcsUri, batchUrlWhenRefreshedByGcsUri.current);
    if (newerBatchUrl != null) {
      // The tile is about to show `newerBatchUrl`. This error is the
      // stored refresh dying, not evidence the file left the library.
      batchUrlWhenRefreshedByGcsUri.current.delete(gcsUri_5);
      setRefreshedUrlByGcsUri(existing_1 => withoutRefreshedUrl(existing_1, gcsUri_5));
      return;
    }
    const failingUrl = refreshedUrlByGcsUri[gcsUri_5] ?? previewUrlByGcsUri[gcsUri_5] ?? null;
    // A URI listed in `failedPreviewGcsUris` never had a real batch sign
    // either — same tile as a full `arePreviewUrlsError` batch failure,
    // just scoped to this one URI (see that prop's own doc comment). Its
    // picture, if any, came from `handleSignErrorRetry`, not the batch, so
    // an error on it must take the same retry-only path below, never the
    // `refreshPreviewUrl` one that can mark it "deleted".
    const batchHadNoUrl = (arePreviewUrlsError || failedPreviewGcsUris.includes(gcsUri_5)) && previewUrlByGcsUri[gcsUri_5] == null;

    // The thumbnail and the lightbox can both fire this for the same
    // gcsUri in the same tick (see the ref's own comment above) — join
    // the in-flight refresh instead of starting a second one, so both
    // callers land on the exact same outcome.
    //
    // A URI the batch never signed must not go through `refreshPreviewUrl`:
    // that writes the shared cache and, while the batch query is still in
    // error, would mark every other unsigned image in the thread deleted.
    const refreshPromise = inFlightRefreshByGcsUri.current.get(gcsUri_5) ?? (batchHadNoUrl ? signPreviewUrlWithoutCaching(gcsUri_5).then(result => {
      if (result?.filename != null) {
        const filename = result.filename;
        setRefreshedFilenameByGcsUri(existing_2 => ({
          ...existing_2,
          [gcsUri_5]: filename
        }));
      }
      return result?.url ?? null;
    }) : refreshPreviewUrl(gcsUri_5));
    inFlightRefreshByGcsUri.current.set(gcsUri_5, refreshPromise);
    void refreshPromise.then(url => {
      inFlightRefreshByGcsUri.current.delete(gcsUri_5);
      if (url == null || url === failingUrl) {
        // Still can't reach Flow: leave the tile a retryable error.
        // A sign that comes back empty after the batch had a URL is the
        // file gone — an expired refresh is not, which is why this is
        // not keyed off "we already refreshed once".
        //
        // The URL the tile is showing is the stored refresh. `isSignError`
        // only holds while `previewUrl` is null, so leaving that refresh
        // in place keeps a dead picture on a success tile.
        if (batchHadNoUrl) {
          batchUrlWhenRefreshedByGcsUri.current.delete(gcsUri_5);
          setRefreshedUrlByGcsUri(existing_3 => withoutRefreshedUrl(existing_3, gcsUri_5));
          return;
        }
        const newerBatchUrl_0 = supersedingBatchUrl(gcsUri_5, refreshedUrlByGcsUri, previewUrlByGcsUri, batchUrlWhenRefreshedByGcsUri.current);
        if (newerBatchUrl_0 != null) return;
        setUnavailableGcsUris(existing_4 => existing_4.includes(gcsUri_5) ? existing_4 : [...existing_4, gcsUri_5]);
        return;
      }
      batchUrlWhenRefreshedByGcsUri.current.set(gcsUri_5, previewUrlByGcsUri[gcsUri_5] ?? null);
      setRefreshedUrlByGcsUri(existing_5 => ({
        ...existing_5,
        [gcsUri_5]: url
      }));
    });
  }, [arePreviewUrlsError, failedPreviewGcsUris, previewUrlByGcsUri, refreshPreviewUrl, refreshedUrlByGcsUri, signPreviewUrlWithoutCaching, unavailableGcsUris]);

  /**
   * Retries a tile whose URL was never signed at all (the *batch* sign
   * request failed outright), as opposed to `handlePreviewError`, which
   * retries a tile whose URL was signed and then stopped working.
   *
   * Deliberately does not reuse `handlePreviewError` for the button: a failed
   * retry here means Flow is still unreachable, so the tile stays a retryable
   * error. `handlePreviewError` is for a URL that was signed and then stopped
   * loading, and only a sign that comes back empty there means the file is
   * gone. It also calls `signPreviewUrlWithoutCaching` rather than
   * `refreshPreviewUrl`: the latter writes into the shared batch query
   * cache, which — while that query is in its own error state with no data
   * — would turn this one URI's success into the *whole* batch's success,
   * silently marking every other still-unsigned image in the thread
   * "deleted" too.
   */
  const handleSignErrorRetry = useCallback((gcsUri_6: string) => {
    void signPreviewUrlWithoutCaching(gcsUri_6).then(result_0 => {
      if (result_0 == null) return;
      batchUrlWhenRefreshedByGcsUri.current.set(gcsUri_6, previewUrlByGcsUri[gcsUri_6] ?? null);
      setRefreshedUrlByGcsUri(existing_6 => ({
        ...existing_6,
        [gcsUri_6]: result_0.url
      }));
      // Same call returns the name; without storing it here too, the tile
      // would show the picture but fall back to "untitled" forever, since
      // filenameByGcsUri only ever comes from the batch, which never held
      // this URI's name to begin with.
      if (result_0.filename != null) {
        setRefreshedFilenameByGcsUri(existing_7 => ({
          ...existing_7,
          [gcsUri_6]: result_0.filename as string
        }));
      }
    });
  }, [previewUrlByGcsUri, signPreviewUrlWithoutCaching]);
  const resolveUrl = useCallback((gcsUri_7: string): string | null => supersedingBatchUrl(gcsUri_7, refreshedUrlByGcsUri, previewUrlByGcsUri, batchUrlWhenRefreshedByGcsUri.current) ?? refreshedUrlByGcsUri[gcsUri_7] ?? previewUrlByGcsUri[gcsUri_7] ?? optimisticPreviewByGcsUri[gcsUri_7]?.previewUrl ?? null, [previewUrlByGcsUri, refreshedUrlByGcsUri, optimisticPreviewByGcsUri]);

  /**
   * A hydrated image part carries only its `gs://` URI, whose last segment is
   * a uuid — never a name. So an unnamed file gets a generic label rather than
   * an identifier the user has no use for.
   */
  const resolveFilename = useCallback((gcsUri_8: string): string => refreshedFilenameByGcsUri[gcsUri_8] ?? filenameByGcsUri[gcsUri_8] ?? optimisticPreviewByGcsUri[gcsUri_8]?.filename ?? tViewer('untitled'), [filenameByGcsUri, refreshedFilenameByGcsUri, optimisticPreviewByGcsUri, tViewer]);
  const handleCloseLightbox = useCallback(() => {
    setOpenedGcsUri(null);
  }, []);
  const attachmentCount = (csvFilename != null ? 1 : 0) + images.length;
  if (attachmentCount === 0) {
    return null;
  }
  const openedUrl = openedGcsUri != null ? resolveUrl(openedGcsUri) : null;
  const isOpenedUnavailable = openedGcsUri != null && unavailableGcsUris.includes(openedGcsUri);

  // Mirrors the image-side `isUnavailable`: only a *settled* index lookup can say a
  // file is gone — while it's still loading, or the listing request itself failed,
  // an absent entry means nothing yet, not deletion.
  const isCsvUnavailable = csvGcsUri != null && !isCsvFilenameIndexLoading && !isCsvFilenameIndexError && csvFilenameByGcsUri[csvGcsUri] == null;
  const tiles: ReactNode = <StyledAttachmentRow data-testid={USER_MESSAGE_ATTACHMENT_ROW_TEST_ID}>
      {csvFilename != null ? <AttachmentTile icon={<DatatableIcon />} unavailableIcon={<NoDatatableIcon />} filename={csvFilename} status="success" isUnavailable={isCsvUnavailable} unavailableTooltip={tCsv('unavailableAria')} size="sm" dataTestId={USER_MESSAGE_CSV_TILE_TEST_ID} /> : null}
      {images.map(image => {
      const previewUrl = resolveUrl(image.gcsUri);
      const hasRetriedOrGivenUp = unavailableGcsUris.includes(image.gcsUri);
      // The sign request itself failed (retries exhausted) rather than
      // settling without this URI — that is a network/server hiccup, not
      // Flow saying the file is gone, so it must not render as "deleted".
      // A retry button gives the user one more shot at exactly this URI.
      const isFailedSign = failedPreviewGcsUris.includes(image.gcsUri);
      const isSignError = (arePreviewUrlsError || isFailedSign) && previewUrl == null && !hasRetriedOrGivenUp;
      // The sign request settled successfully and never had this URI to
      // begin with — Flow omits a URI it cannot or will not sign, so a
      // still-missing one at that point is not "coming soon", it is gone.
      const isUnavailable = hasRetriedOrGivenUp || !arePreviewUrlsLoading && !arePreviewUrlsError && !isFailedSign && previewUrl == null;
      const status = arePreviewUrlsLoading && previewUrl == null ? 'loading' : isSignError ? 'error' : 'success';
      return <AttachmentTile key={image.gcsUri} icon={<ImageIcon />} filename={resolveFilename(image.gcsUri)} previewUrl={previewUrl} status={status} isUnavailable={isUnavailable} size="sm" onRetry={isSignError ? () => {
        handleSignErrorRetry(image.gcsUri);
      } : undefined} onPreviewError={() => {
        handlePreviewError(image.gcsUri);
      }} onActivate={() => {
        setOpenedGcsUri(image.gcsUri);
      }} activateAriaLabel={t('openAria')} unavailableTooltip={t('unavailableAria')} errorTooltip={isSignError ? t('signErrorAria') : undefined} retryAriaLabel={t('retryLoadAria')} dataTestId={userMessageImageTileTestId(image.gcsUri)} />;
    })}
    </StyledAttachmentRow>;
  return <>
      <StyledAttachmentsContainer>
        {attachmentCount === 1 ? tiles : <StyledAttachmentsAccordion Summary={<Typography variant="body3">{tAttachments('accordionSummary', {
          count: attachmentCount
        })}</Typography>} Details={tiles} expandIcon={<StyledExpandIcon>
                <ChevronDownIcon />
              </StyledExpandIcon>} />}
      </StyledAttachmentsContainer>
      {openedGcsUri != null && openedUrl != null && !isOpenedUnavailable ? <CopilotImageLightbox imageUrl={openedUrl} filename={resolveFilename(openedGcsUri)} onClose={handleCloseLightbox} onImageError={() => {
      handlePreviewError(openedGcsUri);
    }} /> : null}
    </>;
};